import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Download, CalendarCheck, Share2, LogIn, Users, RadioTower, ShieldAlert, Crosshair, FileWarning } from "lucide-react";
import axios, { API } from "@/lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { ProbeProgress } from "../components/ProbeProgress";
import { EmailGateModal } from "../components/EmailGateModal";
import { ScoreGauge } from "../components/ScoreGauge";
import { HallucinationWarningCard } from "../components/HallucinationWarningCard";
import { PaywallTiers } from "../components/PaywallTiers";
import { MetadataAuditPanel } from "../components/MetadataAuditPanel";
import { NodeIntegrityPanel } from "../components/NodeIntegrityPanel";
import { ExecutiveIdentityPanel } from "../components/ExecutiveIdentityPanel";
import { SovereignPulsePanel } from "../components/SovereignPulsePanel";
import { TruthVerifyPanel } from "../components/TruthVerifyPanel";
import { CompetitorDeepDive } from "../components/CompetitorDeepDive";
import { AutoRescanToggle } from "../components/AutoRescanToggle";
import { CompetitorTracker } from "../components/CompetitorTracker";
import { Button } from "../components/ui/button";

const TIER_RANK = { free: 0, pro: 1, truth: 2, agency: 3, sovereign_defense: 4 };

function slugify(name) {
  return (name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export const AuditResult = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [audit, setAudit] = useState(null);
  const [fullAudit, setFullAudit] = useState(null);
  const [fullAuditLoaded, setFullAuditLoaded] = useState(false);
  const [showGate, setShowGate] = useState(false);
  const [spots, setSpots] = useState(5);
  const [refreshKey, setRefreshKey] = useState(0);
  const pollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchAudit = async () => {
      const { data } = await axios.get(`${API}/audits/${id}`);
      if (cancelled) return;
      setAudit(data);
      if (user) {
        try {
          const full = await axios.get(`${API}/audits/${id}/full`);
          if (!cancelled) setFullAudit(full.data);
        } catch (e) {
          if (!cancelled) setFullAudit(null);
        }
      }
      if (!cancelled) setFullAuditLoaded(true);
      if (["complete", "failed"].includes(data.status)) {
        clearInterval(pollRef.current);
        if (data.status === "complete" && data.locked) setShowGate(true);
      }
    };
    fetchAudit();
    pollRef.current = setInterval(fetchAudit, 2500);
    return () => {
      cancelled = true;
      clearInterval(pollRef.current);
    };
  }, [id, user, refreshKey]);

  useEffect(() => {
    axios.get(`${API}/agency/spots`).then((res) => setSpots(res.data.spots_remaining)).catch(() => {});
  }, []);

  if (!audit) {
    return <div className="flex min-h-[60vh] items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-sovereign border-t-transparent" /></div>;
  }

  const scanning = ["pending", "scanning", "analyzing"].includes(audit.status);
  const isOwnerView = !!fullAudit;
  const isTrueOwner = !!fullAudit?.is_owner;
  const data = fullAudit || audit;
  const tier = data.paid_tier || "free";
  const hasPro = TIER_RANK[tier] >= TIER_RANK.pro;
  const hasTruth = TIER_RANK[tier] >= TIER_RANK.truth;
  const hasAgency = TIER_RANK[tier] >= TIER_RANK.agency;
  const hasSovereignDefense = TIER_RANK[tier] >= TIER_RANK.sovereign_defense;
  const purchasedButLoggedOut = fullAuditLoaded && !isOwnerView && TIER_RANK[audit.paid_tier || "free"] > TIER_RANK.free;

  return (
    <div className="mx-auto max-w-4xl overflow-x-hidden px-6 py-16" data-testid="audit-result-page">
      <div className="mb-2 flex items-center justify-center gap-3">
        <h1 className="text-center font-heading text-2xl font-bold text-white" data-testid="audit-brand-name">{audit.brand_name}</h1>
        <Link to={`/share/${id}`} className="text-slate-500 hover:text-sovereign" data-testid="share-audit-link" title="Share this audit">
          <Share2 className="h-4 w-4" />
        </Link>
      </div>
      <p className="mb-10 text-center text-sm text-slate-500">AI Visibility Audit</p>

      {scanning && (
        <div className="flex justify-center" data-testid="audit-scanning-view">
          <ProbeProgress status={audit.status} />
        </div>
      )}

      {audit.status === "failed" && <p className="text-center text-sovereign" data-testid="audit-failed-message">Audit failed: {audit.error}</p>}

      {showGate && !user && <EmailGateModal auditId={id} onUnlocked={() => { setShowGate(false); setRefreshKey((k) => k + 1); }} />}

      {!scanning && !audit.locked && (
        <div className="space-y-10 fade-up">
          <div className="flex flex-col items-center gap-8 rounded-lg border border-surface-border bg-surface-card p-10 sm:flex-row sm:justify-around">
            <ScoreGauge score={data.visibility_score ?? 0} />
            <div className="text-center sm:text-left">
              <p className="mb-1 text-xs font-bold uppercase tracking-widest text-sovereign">Sovereign Red Alerts</p>
              <p className="font-heading text-4xl font-extrabold text-white" data-testid="hallucination-count">{data.hallucination_warnings?.length || 0}</p>
              <p className="text-sm text-slate-500">Hallucinations detected across 4 AI models</p>
            </div>
          </div>

          {data.hallucination_warnings?.length > 0 && (
            <div>
              <h2 className="mb-4 font-heading text-xl font-bold text-white">Hallucination Warnings</h2>
              <div className="space-y-4">
                {data.hallucination_warnings.map((w, i) => (
                  <HallucinationWarningCard key={i} warning={w} index={i} />
                ))}
              </div>
              <Link
                to={`/truth/${slugify(audit.brand_name)}`}
                className="mt-4 inline-flex items-center gap-2 text-sm text-openai-cyan hover:text-white"
                data-testid="broadcast-tower-link"
              >
                <RadioTower className="h-4 w-4" /> View Broadcast Tower page for {audit.brand_name}
              </Link>
            </div>
          )}

          {purchasedButLoggedOut && (
            <div className="flex items-center justify-between rounded-lg border border-openai-cyan/40 bg-openai-cyan/5 p-5" data-testid="ownership-login-prompt">
              <p className="text-sm text-slate-300">
                This audit has purchased content ({audit.paid_tier} tier). Log in as the account that purchased it to view the full breakdown.
              </p>
              <Link to={`/login?next=/audit/${id}`}>
                <Button className="gap-2 bg-openai-cyan text-obsidian hover:bg-openai-cyan/90" data-testid="ownership-login-btn">
                  <LogIn className="h-4 w-4" /> Log in
                </Button>
              </Link>
            </div>
          )}

          {!hasSovereignDefense && (
            <div>
              <h2 className="mb-4 font-heading text-xl font-bold text-white">Unlock the Full Signal</h2>
              <PaywallTiers auditId={id} currentTier={tier} spotsRemaining={spots} />
            </div>
          )}

          {isOwnerView && hasPro && (
            <div className="space-y-8 border-t border-surface-border pt-8">
              <div>
                <h2 className="mb-4 font-heading text-xl font-bold text-white">Fix Manual</h2>
                {(data.fix_manual || []).length === 0 ? (
                  <p className="text-sm text-slate-500" data-testid="fix-manual-empty">No fix steps needed - no hallucinations were found to correct.</p>
                ) : (
                  <ul className="space-y-2" data-testid="fix-manual-list">
                    {data.fix_manual.map((step, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                        <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-openai-cyan/20 text-xs font-bold text-openai-cyan">{i + 1}</span>
                        {step}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <h2 className="mb-4 font-heading text-xl font-bold text-white">Competitor Deep-Dive</h2>
                <CompetitorDeepDive competitors={data.competitors_mentioned} />
              </div>
              <MetadataAuditPanel auditId={id} initialResult={data.metadata_audit} readOnly={!isTrueOwner} />
              <NodeIntegrityPanel auditId={id} readOnly={!isTrueOwner} isRootAdmin={!!user?.is_root_admin} />
            </div>
          )}

          {isOwnerView && hasTruth && isTrueOwner && (
            <div className="border-t border-surface-border pt-8">
              <TruthVerifyPanel auditId={id} />
            </div>
          )}
          {isOwnerView && hasTruth && !isTrueOwner && data.truth_verification && (
            <div className="border-t border-surface-border pt-8">
              <h2 className="mb-4 font-heading text-xl font-bold text-white">Verify with Truth (read-only)</h2>
              <p className="text-sm text-slate-400">Accuracy Score: <span className="font-bold text-truth-green">{data.truth_verification.accuracy_score}/100</span></p>
              <p className="text-sm text-slate-400">{data.truth_verification.summary}</p>
            </div>
          )}

          {isOwnerView && hasAgency && (
            <div className="space-y-4 rounded-lg border border-sovereign/40 bg-sovereign/5 p-8" data-testid="agency-unlocked-panel">
              <h2 className="text-center font-heading text-xl font-bold text-white">Sovereign Agency Unlocked</h2>
              {isTrueOwner ? (
                <>
                  <div className="flex flex-wrap justify-center gap-3">
                    <a href={`${API}/audits/${id}/scorecard`} target="_blank" rel="noreferrer" data-testid="download-scorecard-link">
                      <Button className="gap-2 bg-white text-obsidian hover:bg-slate-200">
                        <Download className="h-4 w-4" /> Download PDF Scorecard
                      </Button>
                    </a>
                    <a href={`/booking/${id}`} data-testid="book-consultation-link">
                      <Button className="gap-2 bg-sovereign text-white hover:bg-sovereign/90">
                        <CalendarCheck className="h-4 w-4" /> Book Sovereign Strategy Call
                      </Button>
                    </a>
                    <Link to="/team" data-testid="manage-team-link">
                      <Button variant="outline" className="gap-2 border-surface-border text-slate-200 hover:text-white">
                        <Users className="h-4 w-4" /> Manage Team Seats
                      </Button>
                    </Link>
                  </div>
                  <AutoRescanToggle auditId={id} brandName={audit.brand_name} tier={tier} />
                </>
              ) : (
                <p className="text-center text-sm text-slate-400" data-testid="teammate-readonly-notice">
                  You have read-only teammate access. Scorecard, booking, and team management are owner-only.
                </p>
              )}
            </div>
          )}

          {isOwnerView && hasSovereignDefense && (
            <div className="space-y-6 border-t border-surface-border pt-8" data-testid="sovereign-defense-panel">
              <div className="flex flex-col items-center gap-3 text-center">
                <h2 className="font-heading text-xl font-bold text-warning-amber">Sovereign Defense Intelligence</h2>
                {data.digital_notary && (
                  <div className="rounded-lg border-2 border-[#D4AF37] px-4 py-2" data-testid="digital-notary-seal">
                    <p className="text-xs font-extrabold uppercase tracking-widest text-[#D4AF37]">Director's Verified</p>
                    <p className="text-[10px] text-slate-500">
                      Sealed by {data.digital_notary.verified_by === "founder" ? "The Founder" : "AI Proxy (Buddy Intelligence Officer)"}
                    </p>
                    {data.briefing_id && (
                      <a
                        href={`${API}/audits/${id}/notary-certificate/${data.briefing_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-[#D4AF37] underline hover:text-[#D4AF37]/80"
                        data-testid="download-notary-certificate-link"
                      >
                        <Download className="h-3 w-3" /> Download Certificate
                      </a>
                    )}
                  </div>
                )}
              </div>

              {data.unreported_risk?.is_risk && (
                <div className="rounded-lg border border-sovereign/50 bg-sovereign/10 p-5" data-testid="unreported-risk-banner">
                  <p className="mb-1 flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-wide text-sovereign">
                    <ShieldAlert className="h-4 w-4" /> Unreported Risk Detected
                  </p>
                  <p className="text-sm text-slate-300">{data.unreported_risk.description}</p>
                  <p className="mt-2 text-xs text-slate-500">
                    Fringe sentiment velocity: {data.unreported_risk.velocity_24h} pts/24h &mdash; AI-estimated proxy, not live social data.
                  </p>
                </div>
              )}

              {data.counter_signal && (
                <div className="rounded-lg border border-warning-amber/50 bg-obsidian p-6" data-testid="counter-signal-card">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="rounded-full border border-warning-amber/60 px-3 py-1 text-xs font-bold uppercase tracking-widest text-warning-amber">
                      {data.counter_signal.classification || "SOVEREIGN DEFENSE / EYES ONLY"}
                    </span>
                    <span className="text-xs text-slate-500" data-testid="counter-signal-order-number">{data.counter_signal.order_number}</span>
                  </div>
                  <p className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-white">
                    <FileWarning className="h-5 w-5 text-warning-amber" /> Executive Counter-Signal: Direct Order
                  </p>
                  <p className="mb-4 text-sm text-slate-300">{data.counter_signal.directive}</p>
                  <ol className="mb-4 space-y-3" data-testid="counter-signal-target-sites">
                    {(data.counter_signal.target_sites || []).map((t, i) => (
                      <li key={i} className="rounded-md border border-surface-border bg-surface-card p-3 text-sm">
                        <p className="font-semibold text-white">{i + 1}. {t.site} <span className="ml-2 text-xs font-normal uppercase text-warning-amber">{t.deadline}</span></p>
                        <p className="mt-1 text-slate-400">{t.content_update}</p>
                      </li>
                    ))}
                  </ol>
                  <p className="text-sm font-semibold text-warning-amber">{data.counter_signal.closing_order}</p>
                </div>
              )}

              {data.hijack_alerts?.length > 0 && (
                <div className="space-y-3" data-testid="hijack-alerts-list">
                  {data.hijack_alerts.map((h, i) => (
                    <div key={i} className="rounded-lg border border-sovereign/50 bg-sovereign/10 p-5" data-testid={`hijack-alert-${i}`}>
                      <p className="mb-1 flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-wide text-sovereign">
                        <Crosshair className="h-4 w-4" /> Emergency Hijack Alert: {h.competitor_name}
                      </p>
                      <p className="text-sm text-slate-300">
                        Your score dropped {Math.abs(h.client_delta_pct ?? 0)}% while {h.competitor_name} rose {h.competitor_delta_pct ?? 0}%.
                      </p>
                      <p className="mt-2 text-sm font-semibold text-warning-amber">Keyword Hijack: {h.hijacked_keyword}</p>
                      <p className="text-sm text-slate-400">{h.explanation}</p>
                    </div>
                  ))}
                </div>
              )}

              {isTrueOwner && <CompetitorTracker auditId={id} brandName={audit.brand_name} />}
              {isTrueOwner && <SovereignPulsePanel auditId={id} />}
              {isTrueOwner && <ExecutiveIdentityPanel auditId={id} />}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
